import React, { useEffect, useRef, useState } from 'react';
import { View, Animated, TouchableOpacity } from 'react-native';

import DraggableFlatList, { RenderItemParams } from 'react-native-draggable-flatlist';

import Text from './Text';
import Icon from './Icon';
import Confetti from './Confetti';
import Button from './Button';

import { hexProvider, shadowProvider } from '../utils/hexProvider';
import { translate } from '../services/localization/translations';
import { shuffleArray, sameArrays } from '../utils/helpers';

type Props = {
    quizId: string;
    answers: string[];
    rightQuizzesState: string[];
    wrongQuizzesState: string[];
    setRightQuizzesState: React.Dispatch<React.SetStateAction<string[]>>
    setWrongQuizzesState: React.Dispatch<React.SetStateAction<string[]>>
};

export const SortQuizCard = ({
    quizId,
    answers,
    rightQuizzesState,
    wrongQuizzesState,
    setRightQuizzesState,
    setWrongQuizzesState,
}: Props) => {
    const [firstRenderDone, setFirstRenderDone] = useState<boolean>(false);

    const [submitted, setSubmitted] = useState<boolean>(false);
    const [answeredCorrrect, setAnsweredCorrect] = useState<boolean | undefined>(undefined);
    const [showConfetti, setShowConfetti] = useState<boolean>(false);
    const [showAnswer, setShowAnswer] = useState<boolean>(false);

    const [data, setData] = useState<string[]>(shuffleArray([...answers]));
    const correctOrder: string[] = [...answers];

    useEffect(() => {
        if (firstRenderDone) {
            if (answeredCorrrect === true) {
                setShowConfetti(true);
                const timeOut = setTimeout(() => setShowConfetti(false), 2000);
                return () => {
                    clearTimeout(timeOut);
                };
            }
        } else {
            setFirstRenderDone(true);
        }
    }, [answeredCorrrect])

    const animatedOpacityValueButton = useRef(new Animated.Value(1)).current;
    const animatedYValueButton = useRef(new Animated.Value(1)).current;

    const animatedOpacityValueText = useRef(new Animated.Value(0)).current;
    const animatedYValueText = useRef(new Animated.Value(0)).current;

    const animatedOpacityValueAnswerButton = useRef(new Animated.Value(0)).current;
    const animatedXValueAnswerButton = useRef(new Animated.Value(0)).current;

    const yValButton = animatedYValueButton.interpolate({
        inputRange: [0, 1],
        outputRange: [100, 0]
    });

    const yValtext = animatedYValueText.interpolate({
        inputRange: [0, 1],
        outputRange: [100, 1]
    });

    const xValAnswerButton = animatedXValueAnswerButton.interpolate({
        inputRange: [0, 1],
        outputRange: [-72, 0]
    });

    const showButtonAnimation = () => {
        Animated.parallel([
            Animated.timing(animatedOpacityValueButton, {
                toValue: 1,
                duration: 500,
                useNativeDriver: true
            }),
            Animated.timing(animatedYValueButton, {
                toValue: 1,
                duration: 500,
                useNativeDriver: true
            })
        ]).start()
    }

    const hideButtonAnimation = () => {
        Animated.parallel([
            Animated.timing(animatedOpacityValueButton, {
                toValue: 0,
                duration: 500,
                useNativeDriver: true
            }),
            Animated.timing(animatedYValueButton, {
                toValue: 0,
                duration: 500,
                useNativeDriver: true
            })
        ]).start()
    }

    const hideTextAnimation = () => {
        Animated.parallel([
            Animated.timing(animatedOpacityValueText, {
                toValue: 0,
                duration: 250,
                useNativeDriver: true
            }),
            Animated.timing(animatedYValueText, {
                toValue: 0,
                duration: 250,
                useNativeDriver: true
            })
        ]).start()
    }

    const showTextAnimation = () => {
        Animated.parallel([
            Animated.timing(animatedOpacityValueText, {
                toValue: 1,
                duration: 250,
                useNativeDriver: true
            }),
            Animated.timing(animatedYValueText, {
                toValue: 1,
                duration: 250,
                useNativeDriver: true
            })
        ]).start()
    }

    const showAnswerButtonAnimation = () => {
        Animated.parallel([
            Animated.timing(animatedOpacityValueAnswerButton, {
                toValue: .8,
                duration: 250,
                useNativeDriver: true
            }),
            Animated.timing(animatedXValueAnswerButton, {
                toValue: 1,
                duration: 250,
                useNativeDriver: true
            })
        ]).start()
    }

    const toggleAnswerButtonAnimation = () => {
        Animated.parallel([
            Animated.timing(animatedOpacityValueAnswerButton, {
                toValue: 0,
                duration: 250,
                useNativeDriver: true
            }),
            Animated.timing(animatedXValueAnswerButton, {
                toValue: 0,
                duration: 250,
                useNativeDriver: true
            })
        ]).start(() => setShowAnswer(showAnswer ? false : true));
        Animated.sequence([
            Animated.delay(500),
            Animated.parallel([
                Animated.timing(animatedOpacityValueAnswerButton, {
                    toValue: .8,
                    duration: 250,
                    useNativeDriver: true
                }),
                Animated.timing(animatedXValueAnswerButton, {
                    toValue: 1,
                    duration: 250,
                    useNativeDriver: true
                })
            ])
        ]).start();
    }

    const handleSubmitAnimation = () => {
        Animated.sequence([
            Animated.parallel([
                Animated.timing(animatedOpacityValueButton, {
                    toValue: 0,
                    duration: 500,
                    useNativeDriver: true
                }),
                Animated.timing(animatedYValueButton, {
                    toValue: 0,
                    duration: 500,
                    useNativeDriver: true
                })
            ]),
            Animated.parallel([
                Animated.timing(animatedOpacityValueText, {
                    toValue: 1,
                    duration: 500,
                    useNativeDriver: true
                }),
                Animated.timing(animatedYValueText, {
                    toValue: 1,
                    duration: 500,
                    useNativeDriver: true
                })
            ]),
        ]).start();
    }

    const primaryColor = hexProvider('primary');
    const successColor = hexProvider('success');
    const errorColor = hexProvider('error');
    const greyColor = hexProvider('grey');

    const shadow = shadowProvider();

    const handleSubmitAnswer = () => {
        setSubmitted(true);
        handleSubmitAnimation();
        if (sameArrays(correctOrder, data)) {
            setAnsweredCorrect(true);
            setRightQuizzesState([...rightQuizzesState, quizId]);
        } else {
            setAnsweredCorrect(false);
            setWrongQuizzesState([...wrongQuizzesState, quizId]);
            showAnswerButtonAnimation();
        }
    }

    const handleShowAnswer = () => {
        if (!showAnswer) {
            hideTextAnimation();
            toggleAnswerButtonAnimation();
        } else {
            showTextAnimation();
            toggleAnswerButtonAnimation();
        }
    }

    const renderItem =
        ({ item, index, drag, isActive }: RenderItemParams<string>) => {
            return (
                <TouchableOpacity
                    style={{
                        marginVertical: 2
                    }}
                    disabled={submitted === true ? true : false}
                    onLongPress={drag}>
                    <View
                        style={[
                            {
                                borderRadius: 8 / 1,
                                padding: 16,
                                borderWidth: submitted || isActive ? 4 : undefined,
                                backgroundColor: greyColor,
                                borderColor: submitted ?
                                    correctOrder[index !== undefined ? index : 0] === item ?
                                        successColor :
                                        errorColor :
                                    isActive ?
                                        primaryColor :
                                        undefined
                            },
                            !isActive ? shadow : {}
                        ]}
                    >
                        <View style={{
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}>
                            <View style={{
                                flex: 1,
                                alignItems: 'flex-start',
                                justifyContent: 'center',
                            }}>
                                <Text type='h5' style='normal' textAlign='left' color='text'>{item}</Text>
                            </View>
                            <View style={{
                                alignItems: 'center',
                                justifyContent: 'center',
                                top: 0,
                                bottom: 0,
                                right: 0,
                                zIndex: 1,
                            }}>
                                <Icon
                                    icon={
                                        submitted ?
                                            correctOrder[index !== undefined ? index : 0] === item ?
                                                'check-circle' :
                                                'times-circle' :
                                            'bars'
                                    }
                                    color={
                                        submitted ?
                                            correctOrder[index !== undefined ? index : 0] === item ?
                                                'success' :
                                                'error' :
                                            isActive ?
                                                'primary' :
                                                undefined
                                    }
                                    style={{
                                        padding: 1
                                    }} />
                            </View>
                        </View>
                    </View>
                </TouchableOpacity>
            );
        };

    return (
        <View
            style={{
                flex: 1,
                borderRadius: 8 / 1,
                overflow: 'hidden',
                backgroundColor: greyColor
            }}
        >
            {showConfetti && <Confetti
                icon='check-circle'
                timeInMs={2000}
                zIndex={3}
            />}
            <Animated.View
                style={{
                    flexShrink: 1,
                    position: "absolute",
                    zIndex: 1,
                    top: 0,
                    right: 0,
                    left: 0,
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: animatedOpacityValueAnswerButton,
                    transform: [{
                        translateY: xValAnswerButton
                    }]
                }}>
                <Button
                    type='solid'
                    color={!showAnswer ? 'success' : 'error'}
                    icon={{
                        icon: !showAnswer ? 'check-circle' : 'times-circle',
                        color: 'white',
                        style: {
                            padding: 1
                        }
                    }}
                    style={{
                        height: 64,
                        width: 64,
                        borderRadius: 64 / 2,
                        margin: 8,
                    }}
                    onPress={() => handleShowAnswer()} />
            </Animated.View>
            <DraggableFlatList
                data={showAnswer ? correctOrder : data}
                renderItem={renderItem}
                keyExtractor={(item) => `draggable-item-${item}`}
                onDragBegin={item => hideButtonAnimation()}
                onDragEnd={({ data }) => {
                    setData(data);
                    showButtonAnimation();
                }}
                contentContainerStyle={{ padding: 8 }}
            />
            <Animated.View
                style={{
                    flexShrink: 1,
                    justifyContent: 'center',
                    alignItems: 'center',
                    alignSelf: 'center',
                    marginBottom: 16,
                    position: 'absolute',
                    bottom: 0,
                    zIndex: 2,
                    opacity: animatedOpacityValueButton,
                    transform: [
                        {
                            translateY: yValButton
                        }
                    ],
                }}>
                <Button
                    title={translate('check')}
                    bold={true}
                    icon={{
                        icon: 'check',
                        color: 'white',
                        style: { padding: 8 }
                    }}
                    type='solid'
                    color='primary'
                    textColor='white'
                    style={{ height: 56, borderRadius: 56 / 4 }}
                    onPress={!submitted ? () => handleSubmitAnswer() : undefined}
                />
            </Animated.View>
            <Animated.View
                style={{
                    flexShrink: 1,
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 16,
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1,
                    opacity: animatedOpacityValueText,
                    transform: [
                        {
                            translateY: yValtext
                        }
                    ],
                    backgroundColor: greyColor.concat('CC')
                }}>
                <Text
                    type='h4'
                    style='bold'
                    color={answeredCorrrect ?
                        'success' :
                        'error'} >
                    {answeredCorrrect ?
                        translate('quizRight') :
                        translate('quizWrong')}
                </Text>
            </Animated.View>
        </View>
    );
};

export default SortQuizCard;